<template>
  <div class="sidebar">
    <div class="brand" :class="{ collapsed: !sidebarOpened }">
      <template v-if="logoPreviewUrl">
        <img class="brand-logo" :class="{ collapsed: !sidebarOpened }" :src="logoPreviewUrl" alt="system logo">
      </template>
      <template v-else>
        <span v-if="sidebarOpened">{{ systemName }}</span>
        <span v-else>{{ shortSystemName }}</span>
      </template>
    </div>
    <el-menu
      :default-active="$route.path"
      :collapse="!sidebarOpened"
      :collapse-transition="false"
      background-color="#0f172a"
      text-color="#cbd5e1"
      active-text-color="#ffffff"
      unique-opened
      router
    >
      <sidebar-item
        v-for="route in permissionRoutes"
        :key="route.path"
        :item="route"
        :base-path="route.path"
      />
    </el-menu>
  </div>
</template>

<script>
import SidebarItem from './SidebarItem'
import { getToken } from '@/utils/auth'

const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api'

function buildApiUrl(url) {
  if (/^https?:\/\//.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
    return url
  }
  if (baseUrl === '/api' && url.startsWith('/api/')) {
    return url
  }
  return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`
}

export default {
  components: { SidebarItem },
  data() {
    return {
      logoPreviewUrl: ''
    }
  },
  computed: {
    sidebarOpened() {
      return this.$store.state.app.sidebarOpened
    },
    systemName() {
      return this.$store.state.system.systemName
    },
    systemLogo() {
      return this.$store.state.system.systemLogo
    },
    shortSystemName() {
      const text = this.systemName || '后台'
      return text.slice(0, 2)
    },
    permissionRoutes() {
      return this.$store.state.permission.routes.filter(route => {
        if (route.hidden) {
          return false
        }
        if (route.meta && route.meta.title) {
          return true
        }
        return route.children && route.children.some(child => !child.hidden && child.meta && child.meta.title)
      })
    }
  },
  watch: {
    systemLogo: {
      immediate: true,
      handler() {
        this.refreshLogoPreview()
      }
    }
  },
  beforeDestroy() {
    this.revokeLogoPreview()
  },
  methods: {
    revokeLogoPreview() {
      if (this.logoPreviewUrl && this.logoPreviewUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(this.logoPreviewUrl)
      }
      this.logoPreviewUrl = ''
    },
    async refreshLogoPreview() {
      this.revokeLogoPreview()
      const logoUrl = (this.systemLogo || '').trim()
      if (!logoUrl) {
        return
      }

      if (/^https?:\/\//.test(logoUrl) || logoUrl.startsWith('data:') || logoUrl.startsWith('blob:')) {
        this.logoPreviewUrl = logoUrl
        return
      }

      const token = getToken()
      if (!token) {
        this.logoPreviewUrl = buildApiUrl(logoUrl)
        return
      }

      try {
        const response = await fetch(buildApiUrl(logoUrl), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        if (!response.ok) {
          return
        }
        const blob = await response.blob()
        this.logoPreviewUrl = window.URL.createObjectURL(blob)
      } catch (error) {
        this.logoPreviewUrl = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sidebar {
  min-height: 100%;
  background: #0f172a;

  ::v-deep .el-menu {
    border-right: none;
  }

  ::v-deep .el-menu--collapse {
    width: 64px;
  }

  ::v-deep .el-menu--collapse .el-menu-item span,
  ::v-deep .el-menu--collapse .el-submenu > .el-submenu__title span,
  ::v-deep .el-menu--collapse .el-submenu > .el-submenu__title .el-submenu__icon-arrow {
    display: none !important;
  }

  ::v-deep .el-menu--collapse .el-menu-item,
  ::v-deep .el-menu--collapse .el-submenu > .el-submenu__title {
    padding: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  ::v-deep .el-menu--collapse .el-menu-item i,
  ::v-deep .el-menu--collapse .el-submenu > .el-submenu__title i {
    margin: 0;
    width: auto;
    min-width: 0;
    text-align: center;
    font-size: 18px;
  }
}

.brand {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 12px;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  transition: all 0.25s ease;
}

.brand-logo {
  display: block;
  max-width: 150px;
  max-height: 40px;
  object-fit: contain;
}

.brand-logo.collapsed {
  max-width: 36px;
  max-height: 36px;
}

.brand.collapsed {
  font-size: 16px;
  letter-spacing: 1px;
}
</style>
